import { useMediaQuery } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import Preloader from "../Preloader";
import { useApi } from "../../utils/Api";
import SkillnationExternalSkillsDialog from "./SkillnationExternalSkillsDialog";
import { useNavigate } from "react-router-dom";
import SkillnationDialogSmall from "./SkillnationDialogSmall";
import SkillnationDialogBig from "./SkillnationDialogBig";
import Personnummer from "personnummer";
import { AlertDialog } from "../AlertDialog";
import Translate from "../../utils/Translate";
import { groupBy } from "lodash";
import { useSnackbar } from "notistack";
import { validateEmailWithApi } from "../../utils/email";

async function validateStudentForSkillnation(email, phone, api) {
  const emailValidationResult = await validateEmailWithApi(email, api);
  const isValidEmail = emailValidationResult.isValid;
  const isValidPhone = !isValidEmail && phone && validatePhoneNumber(phone); // No need to check if we have email
  if (!isValidEmail && !isValidPhone) {
    return emailValidationResult.validationErrorTranslationKey
      ? Translate.get(emailValidationResult.validationErrorTranslationKey)
      : Translate.get("EmailOrPhoneRequired");
  }

  return null;
}

export default function SkillnationDialogBase({ courseId, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [externalSkills, setExternalSkills] = useState([]);
  const [userSkills, setUserSkills] = useState([]);
  const [canApproveAll, setCanApproveAll] = useState(true);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [showExternalSkillsDialog, setShowExternalSkillsDialog] = useState(false);
  const [reloadData, setReloadData] = useState(true); // For initial load
  const [validateData, setValidateData] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const isSmallScreen = useMediaQuery("(max-width:910px)");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    if (reloadData) {
      setReloadData(false);
      api
        .fetch(`${import.meta.env.VITE_MAIN_URL}integrations/SsgSkillnation/courses/${courseId}`, false, "GET")
        .then((data) => {
          if (data.isSuccessful) {
            setExternalSkills(data.externalSkills ?? []);
            setUserSkills(data.userSkills ?? []);
            setBookings(data.courseBookings);
            const hasExternalSkills = !!data.externalSkills && data.externalSkills.length > 0;
            setShowExternalSkillsDialog(!hasExternalSkills);
            setValidateData(true);
          }
          setIsDataFetched(true);
        });
    }
  }, [courseId, reloadData, api]);

  function handleReloadData() {
    setIsDataFetched(false);
    setReloadData(true);
  }

  useEffect(() => {
    async function validate() {
      const errorsByCourseParticipantId = {};
      for (const booking of bookings) {
        for (const participant of booking.courseParticipants) {
          const errors = [];
          const validationError = await validateStudentForSkillnation(
            participant.actorEmail,
            participant.mobilePhone,
            api,
          );
          if (!!validationError) {
            errors.push(validationError);
          }
          errorsByCourseParticipantId[participant.courseParticipantId] = errors;
        }
      }

      setBookings((prev) =>
        prev.map((booking) => ({
          ...booking,
          courseParticipants: booking.courseParticipants.map((cp) => ({
            ...cp,
            errors: errorsByCourseParticipantId[cp.courseParticipantId],
          })),
        })),
      );
    }

    if (validateData) {
      setValidateData(false);
      validate();
    }
  }, [validateData, bookings, api]);

  // Updates the disabled status of the "Approve All" button,
  // based on whether there are any skills remaining that are
  // not yet approved, and are not locked by an existing license
  useEffect(() => {
    const allStudents = bookings.flatMap((b) => b.courseParticipants);
    const someStudentHasNotAllSkills = allStudents.some((student) =>
      externalSkills.some(
        (es) => !userSkills.some((us) => us.actorId === student.actorId && us.code === es.code && !us.isDeleted),
      ),
    );
    setCanApproveAll(someStudentHasNotAllSkills);
  }, [userSkills, externalSkills, bookings]);

  async function handleSave() {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}integrations/SsgSkillnation/courses/${courseId}/skills`,
      userSkills,
      "PUT",
    );
    if (response.isSuccessful) {
      if (response.errors && response.errors.length > 0) {
        const allStudents = bookings.flatMap((b) => b.courseParticipants);
        const studentErrors = Object.entries(groupBy(response.errors, "actorId")).map(([actorId, errors]) => ({
          student: allStudents.find((a) => a.actorId == actorId),
          errors: errors,
        }));
        const errorText = studentErrors
          .map((ae) => {
            const errorsText = ae.errors.map((e) => e.errorMessage).join("\n\n");
            return `${ae.student.name}:\n\n${errorsText}`;
          })
          .join("\n\n\n");
        setErrorMessage(errorText);
        setReloadData(true);
      } else {
        const successMessage = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(successMessage),
        });
        onClose();
      }
    } else {
      setReloadData(true);
    }
  }

  function handleCheckSkillnationSkill(event, actorId, courseParticipantId, code) {
    const index = userSkills.findIndex(
      (x) => x.actorId === actorId && x.courseParticipantId === courseParticipantId && x.code === code,
    );
    if (index > -1) {
      setUserSkills(
        (prev) =>
          prev[index].ssgUserSkillId
            ? prev.with(index, {
                ...prev[index],
                isDeleted: !prev[index].isDeleted, // Toggle isDeleted
              })
            : prev.toSpliced(index, 1), // Remove the entire skill
      );
    } else {
      // Add new skill
      setUserSkills((prev) => [
        ...prev,
        {
          actorId,
          courseParticipantId,
          code,
        },
      ]);
    }
  }

  function handleCheckSkillAllChange(event, code) {
    const allStudents = bookings.flatMap((b) => b.courseParticipants);
    const studentsWithoutSkill = allStudents.filter(
      (student) => !userSkills.some((x) => x.code === code && x.actorId === student.actorId && !x.isDeleted),
    );
    if (studentsWithoutSkill.length > 0) {
      for (let student of studentsWithoutSkill) {
        handleCheckSkillnationSkill(null, student.actorId, student.courseParticipantId, code);
      }
    } else {
      for (let student of allStudents) {
        handleCheckSkillnationSkill(null, student.actorId, student.courseParticipantId, code);
      }
    }
  }

  function handleApproveAllSkills() {
    const allStudents = bookings.flatMap((b) => b.courseParticipants);
    for (const externalSkill of externalSkills) {
      const studentsWithoutSkill = allStudents.filter(
        (student) =>
          !userSkills.some((x) => x.code === externalSkill.code && x.actorId === student.actorId && !x.isDeleted),
      );
      for (let student of studentsWithoutSkill) {
        handleCheckSkillnationSkill(null, student.actorId, student.courseParticipantId, externalSkill.code);
      }
    }
  }

  function handleApproveAllSkillsForStudent(actorId, courseParticipantId) {
    const missingSkills = externalSkills.filter(
      (es) => !userSkills.some((x) => x.code === es.code && x.actorId === actorId && !x.isDeleted),
    );

    for (let skill of missingSkills) {
      handleCheckSkillnationSkill(null, actorId, courseParticipantId, skill.code);
    }
  }

  function handleOpenExternalSkillsDialog() {
    setShowExternalSkillsDialog(true);
  }

  function handleCloseExternalSkillsDialog() {
    setShowExternalSkillsDialog(false);
    setReloadData(true);
  }

  function hasErrors() {
    return bookings.some((b) =>
      b.courseParticipants.some((cp) => !!cp.errors && cp.errors.length > 0 && cp.errors.some((e) => !!e)),
    );
  }

  function canSave() {
    if (hasErrors()) return false;

    const hasUserSkillsToAdd = userSkills.filter((x) => !x.ssgUserSkillId && !x.isDeleted).length > 0;
    const hasUserSkillsToRemove = userSkills.filter((x) => !!x.ssgUserSkillId && !!x.isDeleted).length > 0;
    return hasUserSkillsToAdd || hasUserSkillsToRemove;
  }

  const commonProps = {
    onClose,
    bookings,
    userSkills,
    externalSkills,
    handleCheckSkillnationSkill,
    canApproveAll,
    handleApproveAllSkills,
    handleOpenAllTrainingsDialog: handleOpenExternalSkillsDialog,
    showExternalSkillsDialog: showExternalSkillsDialog,
    handleApproveAllSkillsForStudent,
    handleSave,
    handleReloadData,
    hasErrors,
    canSave,
  };
  const bigProps = {
    ...commonProps,
    handleCheckSkillAllChange,
  };
  const smallProps = {
    ...commonProps,
  };
  
  return (
    <>
      {errorMessage && (
        <AlertDialog
          open
          titleText={Translate.get("SomethingFailed")}
          bodyText={errorMessage}
          buttonText={Translate.get("Close")}
          onClose={() => setErrorMessage(null)}
          showCopyButton
          zIndexOverride={1300}
        />
      )}
      {showExternalSkillsDialog && (
        <SkillnationExternalSkillsDialog courseId={courseId} onClose={handleCloseExternalSkillsDialog} />
      )}

      {!isDataFetched && <Preloader zIndexOverride={4000} />}
      {isDataFetched && !isSmallScreen && <SkillnationDialogBig {...bigProps} />}
      {isDataFetched && isSmallScreen && <SkillnationDialogSmall {...smallProps} />}
    </>
  );
}
